import { Alert, AlertProps, Snackbar } from '@mui/material';
import React, { createContext, useContext, useState, PropsWithChildren } from 'react';

interface NotificationContextState {
    alert: AlertProps | null;
    setAlert: (alert: AlertProps | null) => void;
}

const defaultNotificationContextState: NotificationContextState = {
    alert: null,
    setAlert: () => { },
};

const NotificationContext = createContext(
    defaultNotificationContextState
);
const NotificationSnackBar: React.FC = () => {
    const { alert, setAlert } = useContext(NotificationContext);
    const handleCloseSnackBar = (_?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setAlert(null);
    };
    return (
        alert && (
            <Snackbar
                open
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                onClose={handleCloseSnackBar}
                autoHideDuration={3000}
                data-testid='global-snackbar'
            >
                <Alert
                    variant='filled'
                    {...alert}
                    onClose={handleCloseSnackBar}
                />

            </Snackbar>
        )
    );
};
export interface NotificationProps {
    message: string;
    severity: AlertProps['severity']
}
const useNotification = () => {
    const { setAlert } = React.useContext(NotificationContext);
    const setNotification = (props: NotificationProps) =>
        setAlert({ children: props.message, severity: props.severity });
    const clearNotification = () => setAlert(null);

    const setSuccessNotification = (message: string) =>
        setNotification({ message, severity: 'success' });
    const setErrorNotification = (message: string) =>
        setNotification({ message, severity: 'error' });
    const setWarningNotification = (message: string) =>
        setNotification({ message, severity: 'warning' });
    const setInfoNotification = (message: string) =>
        setNotification({ message, severity: 'info' });
    return {
        setNotification,
        setSuccessNotification,
        setErrorNotification,
        setWarningNotification,
        clearNotification,
        setInfoNotification,
    };
};
const NotificationProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [alert, setAlert] = useState(defaultNotificationContextState.alert);
    return (
        <NotificationContext.Provider value={{ alert, setAlert }}>
            <NotificationSnackBar />
            {children}
        </NotificationContext.Provider>
    );
};
export { NotificationProvider, useNotification };
